import utils from "./utils";
import { constants } from "./constants";
import FCLoggger from "../../fclogger";
import TAUI from "./ui/ta_ui";

/* Use provided credentials OR configured credentials and return
   a full connection object */
export const connectParams = function (params, collectedParams) {
  // clone the original params to avoid permanently destroying them
  let clonedParams = Object.assign({}, params);

  // Order of importance logic is in reverse.
  // 3rd - use configured credentials (if no pgpMessage, cleartext will be priority)
  // 2nd - back end itself will prioritize pgpMessage if present (no change needed)
  // 1st - if creds are collected from login form, prioritize by mutating params
  //       and ALSO deleting a pgpMessage value if present.

  if (
    typeof collectedParams == "object" &&
    typeof collectedParams.username == "string"
  ) {
    clonedParams.username = collectedParams.username;
    clonedParams.password = collectedParams.password;
    clonedParams.pgpMessage.length = 0;
  }

  // return a likely-to-not-work param set if "valid", but warn via console.
  if (
    clonedParams.username.length == 0 &&
    clonedParams.password.length == 0 &&
    clonedParams.pgpMessage.length == 0
  ) {
    console.error(
      "params collected for connection contain empty values and likely will not work."
    );
  }
  return clonedParams;
};

export const mergeTokensWithConnectParams = (
  tokens,
  connectParams,
  openIDConfig
) => {
  const { idToken } = tokens.idToken;
  const { accessToken } = tokens.accessToken;
  const { refreshToken } = tokens.refreshToken;
  const { nonce } = tokens;
  const filteredParams = Object.fromEntries(
    Object.entries(connectParams).filter(([_, v]) => v != null && v !== "")
  );
  return {
    nonce,
    idToken,
    accessToken,
    refreshToken,
    refreshURI: openIDConfig.refreshUri,
    clientID: openIDConfig.clientId,
    ...filteredParams,
  };
};

export const abstractConnect = async (basePath, queryStringParams) => {
  let responseData = undefined;
  try {
    TAUI.spinnerScreen(true, "connecting");
    responseData = await connect(
      basePath + "/rs/agent/connect",
      queryStringParams
    );
    return {
      connectionKey: responseData?.connectionKey,
      error: responseData.reason?.text,
    };
  } catch (error) {
    console.error(`abstract connect error : ${error}`);
    return { error: error.message, connectionKey: undefined };
  } finally {
    TAUI.spinnerScreen(false, "connecting");
  }
};

/**
 * If a successful connection is made and validated, the back end
 * returns a key and we may redirect. The target page will need to
 * know the token/key being used for data interchange.
 *
 * @param {String} key - The key ultimately supplied by the back end
 *                       for the application to use during interchange
 */
export const redirectAfterConnect = (key) => {
  // first store the connection key in the local storage
  if (key) {
    TAUI.spinnerScreen(true, "redirecting");
    window.localStorage.setItem(constants.connectionkey, key);
    window.location = utils.getRedirectPage();
  } else {
    const errorMessage =
      "Missing connectionkey while trying to redirect to express page! ";
    FCLoggger.info(errorMessage);
    throw new Error(errorMessage);
  }
};

/* even though the connect URL is "known" to humans, it is not in scope,
   so it must be passed in. Regardless, other sanity checks are specific
   to the connect call, so this is a connect-only function
   */

export async function connect(url, params) {
  url = new URL(url);
  url.search = new URLSearchParams(params).toString();

  // later, will be conditionally modified before finally being passed into the Ajax call
  let fetchOptions = {
    method: "GET",
    cache: "no-cache",
  };

  // if there is a pgpMessage present, switch type to POST, which the TransferAgent
  // will recognize as being the REST call for use with pgpMessage
  if (typeof params.pgpMessage !== "undefined") {
    if (params.pgpMessage.length > 0) {
      // pgpMessage can come in as an array of strings (one per line of the message)

      if ($.isArray(params.pgpMessage)) {
        params.pgpMessage = params.pgpMessage.join("\n");
      }

      // could pgpMessage be popped out after processing?
      params.MungedConnectionRequest = params.pgpMessage;

      fetchOptions.method = "POST";
      fetchOptions.headers = { "Content-Type": "application/json" };
      fetchOptions.body = JSON.stringify(params);
    }
  }
  const response = await fetch(url, fetchOptions);
  console.info(`got a response to connect call`);
  return response.json();
}

export async function isValidConnectionKeyPresent(
  basePath,
  testLogin = "false"
) {
  let connectionKey = localStorage.getItem(constants.connectionkey);
  FCLoggger.info(`checking if connection key ${connectionKey} is valid`);
  if (connectionKey) {
    try {
      const response = await fetch(
        `${basePath}/rs/agent/connect/${connectionKey}?testLogin=${testLogin}`
      );
      FCLoggger.debug(
        `response for validating connection key : ${response.statusText}`
      );
      return response.ok;
    } catch (err) {
      FCLoggger.info(
        `Error occured while checking for valid connection key : ${err}`
      );
    }
  }
  return false;
}
